import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HeadphoneStatus } from '../../utils/audioDevices';
import './HeadphoneCheckModal.css';

interface Props {
  isOpen: boolean;
  status: HeadphoneStatus;
  detectedLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const CheckIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const WarningIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const HeadphoneCheckModal: React.FC<Props> = ({ isOpen, status, detectedLabel, onConfirm, onCancel }) => {
  const { t } = useTranslation('learning');
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => confirmRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const isDetected = status === 'detected' && !!detectedLabel;
  const confirmLabel = isDetected
    ? t('shadowing.headphoneCheck.start')
    : t('shadowing.headphoneCheck.continueAnyway');

  return (
    <div className="hp-check-overlay" onClick={onCancel}>
      <div
        className="hp-check-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="hp-check-title"
      >
        <div className="hp-check-header">
          <h3 id="hp-check-title">{t('shadowing.headphoneCheck.title')}</h3>
        </div>

        <div className="hp-check-body">
          {isDetected ? (
            <div className="hp-check-status hp-check-status--ok">
              <span className="hp-check-icon"><CheckIcon /></span>
              <div className="hp-check-status-text">
                <strong>{t('shadowing.headphoneCheck.detected')}</strong>
                <span className="hp-check-device">{detectedLabel}</span>
              </div>
            </div>
          ) : (
            <div className="hp-check-status hp-check-status--warn">
              <span className="hp-check-icon"><WarningIcon /></span>
              <div className="hp-check-status-text">
                <strong>
                  {status === 'not_detected'
                    ? t('shadowing.headphoneCheck.notDetected')
                    : t('shadowing.headphoneCheck.unknown')}
                </strong>
              </div>
            </div>
          )}
          <p className="hp-check-note">{t('shadowing.headphoneCheck.note')}</p>
        </div>

        <div className="hp-check-footer">
          <button
            type="button"
            className="hp-check-btn hp-check-btn--cancel"
            onClick={onCancel}
          >
            {t('shadowing.headphoneCheck.cancel')}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className="hp-check-btn hp-check-btn--primary"
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeadphoneCheckModal;
